import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, domAnimation, LazyMotion, m } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCat, faCheck, faDog, faPaw } from "@fortawesome/pro-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { ResponsiveModalSheet } from "@/shared/components/ResponsiveModalSheet";
import { Button, Heading } from "@/shared/components/ui";
import { IconButton } from "@/shared/components/ui/IconButton";
import Strings from "@/shared/utils/Strings.constants";
import { CurrencyUtils } from "@/shared/utils/CurrencyUtils";
import { UIUtils } from "@/shared/utils/UIUtils";
import { cn } from "@/shared/utils";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { BreakpointUtils } from "@/shared/utils/BreakpointUtils";
import { RecommendedPlanGroup, RecommendedPlanId, ResolvedRecommendedPlan } from "./MultiPlanGuideUtils";

// "Choose your pet plan" modal — one card group per scope (pet or "N pets"),
// shared-label plan table on md+, stacked cards (most-expensive-first) below md. After
// a plan is applied the group collapses to a summary row with an Edit pill that re-expands it.
export type SelectedPlansByGroup = Partial<Record<string, RecommendedPlanId>>;

interface MultiPlanGuideModalProps {
    isOpen: boolean;
    onClose: () => void;
    groups: RecommendedPlanGroup[];
    selectedPlans: SelectedPlansByGroup;
    onSelectPlan: (group: RecommendedPlanGroup, plan: ResolvedRecommendedPlan) => Promise<void>;
}

// whole-dollar amounts (limits/deductibles); formatCurrency also guards NaN/undefined
const formatLimit = (value: number) => UIUtils.formatCurrency(value, { min: 0, max: 0 });

// How long the "Selected" button stays visible before the group animates closed
const COLLAPSE_DELAY_MS = 800;

const groupIcon = (group: RecommendedPlanGroup) => {
    if (group.petIds.length > 1) return faPaw;
    return group.species === "Cat" ? faCat : faDog;
};

const GroupHeader: React.FC<{ group: RecommendedPlanGroup }> = ({ group }) => (
    <p className="flex items-center gap-2 text-base font-bold leading-6 text-content-primary">
        <FontAwesomeIcon icon={groupIcon(group)} className="size-5 shrink-0" />
        {group.header}
    </p>
);

// One row per coverage attribute — shared by the stacked cards (labels inside each
// card, below md) and the md+ table (labels rendered once per group)
const PLAN_ROWS: { label: string; value: (plan: ResolvedRecommendedPlan) => React.ReactNode }[] = [
    { label: Strings.MULTI_PLAN_GUIDE.COVERAGE_LABEL, value: () => Strings.MULTI_PLAN_GUIDE.ACCIDENT_ILLNESS },
    { label: Strings.MULTI_PLAN_GUIDE.REIMBURSEMENT, value: plan => `${plan.reimbursementRate}%` },
    { label: Strings.MULTI_PLAN_GUIDE.ANNUAL_LIMIT, value: plan => formatLimit(plan.annualLimit) },
    { label: Strings.MULTI_PLAN_GUIDE.ANNUAL_DEDUCTIBLE, value: plan => formatLimit(plan.annualDeductible) }
];

const SelectButton: React.FC<{
    group: RecommendedPlanGroup;
    plan: ResolvedRecommendedPlan;
    isSelected: boolean;
    isUpdating?: boolean;
    onSelect: () => void;
}> = ({ group, plan, isSelected, isUpdating, onSelect }) => (
    <Button
        variant="default-rounded"
        size="sm"
        data-testid={`multi-plan-select-${group.groupKey}-${plan.id}`}
        onClick={onSelect}
        disabled={isUpdating || isSelected}
        className={cn("mt-auto w-full px-2 text-sm", isSelected && "bg-background-success-secondary text-content-primary-invert disabled:opacity-100")}
    >
        {isSelected ? (
            <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faCheck} className="size-4" />
                {Strings.MULTI_PLAN_GUIDE.SELECTED}
            </span>
        ) : (
            Strings.MULTI_PLAN_GUIDE.SELECT
        )}
    </Button>
);

const PlanPrice: React.FC<{ group: RecommendedPlanGroup; plan: ResolvedRecommendedPlan }> = ({ group, plan }) => (
    <p className="whitespace-nowrap text-center">
        <span data-testid={`multi-plan-price-${group.groupKey}-${plan.id}`} className="text-2xl font-bold tracking-tight text-content-primary">
            {CurrencyUtils.format(plan.price)}
        </span>
        <span className="text-base font-medium text-content-primary">{Strings.MULTI_PLAN_GUIDE.PER_MONTH}</span>
    </p>
);

// Below md: self-contained stacked card with its own label column
const PlanCard: React.FC<{
    group: RecommendedPlanGroup;
    plan: ResolvedRecommendedPlan;
    isSelected: boolean;
    isUpdating?: boolean;
    onSelect: () => void;
}> = ({ group, plan, isSelected, isUpdating, onSelect }) => {
    return (
        <div data-testid={`multi-plan-card-${plan.id}`} className={cn("flex flex-col gap-3 rounded-xl p-4", plan.highlighted && "bg-background-brand-b-secondary")}>
            <PlanPrice group={group} plan={plan} />
            <dl className="flex flex-col gap-2">
                {PLAN_ROWS.map(row => (
                    <div key={row.label} className="flex items-center justify-between gap-4 text-sm leading-5">
                        <dt className="font-medium text-content-secondary">{row.label}</dt>
                        <dd className="text-right font-bold text-content-primary">{row.value(plan)}</dd>
                    </div>
                ))}
            </dl>
            <SelectButton group={group} plan={plan} isSelected={isSelected} isUpdating={isUpdating} onSelect={onSelect} />
        </div>
    );
};

// md+: one shared label column per group, plans as value columns (Figma 74829:55481).
// Subgrid keeps a wrapper div per plan (teal highlight + testid) while every column
// shares the parent's rows: price / one per PLAN_ROWS / Select.
const PLAN_TABLE_ROW_COUNT = PLAN_ROWS.length + 2;

const PlanTable: React.FC<{
    group: RecommendedPlanGroup;
    selectedPlanId?: string;
    isUpdating?: boolean;
    onSelect: (plan: ResolvedRecommendedPlan) => void;
}> = ({ group, selectedPlanId, isUpdating, onSelect }) => (
    <div className="grid grid-cols-[auto_repeat(3,minmax(0,1fr))] gap-x-2" style={{ gridTemplateRows: `repeat(${PLAN_TABLE_ROW_COUNT}, auto)` }}>
        <div className="grid grid-rows-subgrid py-4" style={{ gridRow: `span ${PLAN_TABLE_ROW_COUNT}` }}>
            <div />
            {PLAN_ROWS.map(row => (
                <div key={row.label} className="flex items-center text-sm font-medium leading-5 text-content-secondary">
                    {row.label}
                </div>
            ))}
            <div />
        </div>
        {group.plans.map(plan => (
            <div
                key={plan.id}
                data-testid={`multi-plan-card-${plan.id}`}
                className={cn("grid grid-rows-subgrid gap-y-3 rounded-xl p-4", plan.highlighted && "w-fit bg-background-brand-b-secondary")}
                style={{ gridRow: `span ${PLAN_TABLE_ROW_COUNT}` }}
            >
                <div className="flex items-center justify-center">
                    <PlanPrice group={group} plan={plan} />
                </div>
                {PLAN_ROWS.map(row => (
                    <div key={row.label} className="flex items-center justify-center text-center text-sm font-bold leading-5 text-content-primary">
                        {row.value(plan)}
                    </div>
                ))}
                <SelectButton group={group} plan={plan} isSelected={selectedPlanId === plan.id} isUpdating={isUpdating} onSelect={() => onSelect(plan)} />
            </div>
        ))}
    </div>
);

export const MultiPlanGuideModal: React.FC<MultiPlanGuideModalProps> = ({ isOpen, onClose, groups, selectedPlans, onSelectPlan }) => {
    const {
        appState: { isQuoteUpdating, breakpoint }
    } = useAppLayerContext();

    // D4: groups collapse to a summary row after a plan is applied; Edit re-expands
    const [collapsedGroups, setCollapsedGroups] = useState<Partial<Record<string, boolean>>>({});
    // One pending timer per group: re-selecting cancels the group's previous timer instead
    // of queueing a second one, so the map can never exceed the group count.
    const collapseTimersRef = useRef<Partial<Record<string, ReturnType<typeof setTimeout>>>>({});
    useEffect(
        () => () => {
            Object.values(collapseTimersRef.current).forEach(timer => timer && clearTimeout(timer));
        },
        []
    );

    const isMultiTitle = groups.length > 1 || (groups[0]?.petIds.length ?? 0) > 1;
    // isMobile switches the sheet chrome (top sheet + X button, base–xs). The plan layout
    // switches independently at md: stacked cards (most-expensive-first) below md, the
    // shared-label table from md up — the horizontal layout never renders at sm.
    const isMobile = !!breakpoint && BreakpointUtils.isMobile(breakpoint);
    const isMdUp = !!breakpoint && BreakpointUtils.isWithin({ currentBreakpoint: breakpoint, start: "md" });

    // Optimistic select: the parent flips the button to "Selected" synchronously and runs
    // the apply in the background (a failure rolls the selection back upstream, and since
    // isCollapsed requires a selected plan the group re-expands on its own). The collapse
    // is delayed just long enough for the user to register the Selected state.
    const handleSelect = (group: RecommendedPlanGroup, plan: ResolvedRecommendedPlan) => {
        void onSelectPlan(group, plan);
        const pendingTimer = collapseTimersRef.current[group.groupKey];
        if (pendingTimer) clearTimeout(pendingTimer);
        collapseTimersRef.current[group.groupKey] = setTimeout(() => setCollapsedGroups(previous => ({ ...previous, [group.groupKey]: true })), COLLAPSE_DELAY_MS);
    };

    const expandGroup = (group: RecommendedPlanGroup) => {
        setCollapsedGroups(previous => ({ ...previous, [group.groupKey]: false }));
    };

    const selectedPlanFor = (group: RecommendedPlanGroup) => group.plans.find(plan => plan.id === selectedPlans[group.groupKey]);

    return (
        <ResponsiveModalSheet
            isOpen={isOpen}
            onClose={onClose}
            sheetSide="top"
            showCloseButton={false}
            title={
                <div className="flex items-start justify-between gap-2 text-left">
                    <div className="mb-4 flex flex-col gap-1 md:mb-0">
                        <Heading level="h3" className="text-lg font-bold leading-7 text-content-primary">
                            {isMultiTitle ? Strings.MULTI_PLAN_GUIDE.MODAL_TITLE_MULTI : Strings.MULTI_PLAN_GUIDE.MODAL_TITLE_SINGLE}
                        </Heading>
                        <p className="text-sm font-normal leading-5 text-content-secondary">{Strings.MULTI_PLAN_GUIDE.MODAL_SUBTITLE}</p>
                    </div>
                    {/* The sheet has no built-in top-right X (SheetWrapper's close is a bottom
                        button); the desktop ModalWrapper renders its own, so mobile only.
                        p-3 keeps the tap target ~40px rather than the bare 16px icon. */}
                    {isMobile && (
                        <IconButton aria-label={Strings.CLOSE} data-testid="multi-plan-guide-close" onClick={onClose} className="-m-2 shrink-0 rounded-full p-3">
                            <FontAwesomeIcon icon={faXmark} className="size-4 text-content-secondary" />
                        </IconButton>
                    )}
                </div>
            }
            styles={{
                // ModalWrapper's built-in X is a bare 16px icon at 70% opacity — easy to miss.
                // Pad it to a ~40px target and drop the dimming so the exit is obvious.
                modal: { content: "flex-col sm:max-w-[620px] md:max-w-2xl", closeButton: "p-3 opacity-100 text-content-secondary rounded-full" },
                sheet: { content: "!h-[100dvh] overflow-y-auto" }
            }}
        >
            <LazyMotion features={domAnimation}>
                <div data-testid="multi-plan-guide-modal" className="flex flex-col gap-4">
                    {groups.map(group => {
                        const selectedPlan = selectedPlanFor(group);
                        const isCollapsed = !!collapsedGroups[group.groupKey] && !!selectedPlan;

                        return (
                            <div key={group.groupKey} data-testid={`multi-plan-group-${group.groupKey}`} className="flex flex-col rounded-xl border border-stroke-primary p-4">
                                {/* smooth swap: the plan rows shrink away, then the summary row fades in (and back on Edit) */}
                                <AnimatePresence initial={false} mode="wait">
                                    {isCollapsed ? (
                                        <m.div
                                            key="collapsed"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            data-testid={`multi-plan-collapsed-${group.groupKey}`}
                                            className="flex items-center justify-between gap-2"
                                        >
                                            <GroupHeader group={group} />
                                            <button
                                                type="button"
                                                data-testid={`multi-plan-edit-${group.groupKey}`}
                                                onClick={() => expandGroup(group)}
                                                className="flex items-center gap-2 rounded-full bg-background-success-secondary px-3 py-1 text-sm font-bold leading-5 text-content-primary-invert"
                                            >
                                                {CurrencyUtils.format(selectedPlan.price)}
                                                {Strings.MULTI_PLAN_GUIDE.PER_MONTH}
                                                <span className="font-normal underline">{Strings.MULTI_PLAN_GUIDE.EDIT}</span>
                                            </button>
                                        </m.div>
                                    ) : (
                                        <m.div
                                            key="expanded"
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.3, ease: "easeInOut" }}
                                            className="flex flex-col overflow-hidden"
                                        >
                                            <GroupHeader group={group} />
                                            {!isMdUp ? (
                                                <div className="mt-3 flex flex-col gap-3">
                                                    {/* stacked layout leads with the most expensive plan, per the Figma mobile modal */}
                                                    {[...group.plans].reverse().map(plan => (
                                                        <PlanCard
                                                            key={plan.id}
                                                            group={group}
                                                            plan={plan}
                                                            isSelected={selectedPlans[group.groupKey] === plan.id}
                                                            isUpdating={isQuoteUpdating}
                                                            onSelect={() => handleSelect(group, plan)}
                                                        />
                                                    ))}
                                                </div>
                                            ) : (
                                                <PlanTable
                                                    group={group}
                                                    selectedPlanId={selectedPlans[group.groupKey]}
                                                    isUpdating={isQuoteUpdating}
                                                    onSelect={plan => handleSelect(group, plan)}
                                                />
                                            )}
                                        </m.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                    <Button variant="outline" data-testid="multi-plan-guide-done" onClick={onClose} className="mx-auto rounded-full px-8">
                        {Strings.MULTI_PLAN_GUIDE.DONE}
                    </Button>
                </div>
            </LazyMotion>
        </ResponsiveModalSheet>
    );
};

MultiPlanGuideModal.displayName = "MultiPlanGuideModal";
